<script lang="ts" context="module">
    import {
        createListItem,
        createRoot,
        type Field,
        Form,
    } from '@jmvdevelop/svelte-form';

    type Value = number | null;

    export type InnerFields = {
        self: Field<Value[], Value[]>;
        item: (index: number) => Field<Value[], Value>;
    };

    function createInnerFields() {
        let root = createRoot<Value[]>();

        return {
            self: root,
            item: (index: number) => createListItem(index, root, null),
        };
    }

    function normalizeInnerValues(innerValues: Value[]): Value[] {
        let encounteredNull = false;
        return innerValues.map((value) => {
            if (value === null || encounteredNull) {
                encounteredNull = true;
                return null;
            } else {
                return value;
            }
        });
    }

    export function createInnerForm(
        id: string,
        initialValues: Value[],
    ): [Form<Value[], string>, InnerFields] {
        const fields = createInnerFields();
        const form = new Form<Value[], string>({ initialValues }, id);

        form.addOnChangeValues((_changedFields, prevState, nextState) => {
            const prev = fields.self.getValue(prevState.values);
            const next = normalizeInnerValues(fields.self.getValue(nextState.values));

            if (prev != next) {
                return { ...nextState, values: next };
            } else {
                return nextState;
            }
        });

        return [form, fields];
    }
</script>

<script lang="ts" generics="TValues">
    import { query } from '$lib/apollo/SvelteApollo';
    import { SelectCategoryInHierarchyQueryDocument } from '$gql/graphql';
    import Spinner from '$components/Spinner/Spinner.svelte';
    import groupBy from 'lodash/groupBy';
    import sortBy from 'lodash/sortBy';
    import last from 'lodash/last';
    import Select from '$components/Form/Select.svelte';
    import keyBy from 'lodash/keyBy';
    import { keepNotNull } from '$lib/utils/filterNotNull.js';
    import { onMount } from 'svelte';
    import { cx } from '$lib/utils/cx';
    import { match, P } from 'ts-pattern';
    import type { FormState } from '@jmvdevelop/svelte-form/Types';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, Value>;

    export let validation:
        | { type: 'minimum-level'; level: number }
        | 'force-leaf'
        | null = null;

    export let parentId: number | null = null;
    export let id: string | null = null;

    let className: string = '';
    export { className as class };

    const value = form.stores().fieldValue(field);

    let [innerForm, innerFields] = createInnerForm(`${id}-inner`, []);
    let innerValues = innerForm.stores().fieldValue(innerFields.self);

    let { result: categoriesResult } = query(SelectCategoryInHierarchyQueryDocument, {});
    let categories = $categoriesResult.data?.categories ?? [];
    $: categories = $categoriesResult.data?.categories ?? [];
    $: categoriesByLevel = groupBy(categories ?? [], (c) => c.levelInHierarchy);
    $: categoriesById = keyBy(categories ?? [], (c) => c.id);
    $: levels = sortBy(
        Object.keys(categoriesByLevel).map((i) => parseInt(i, 10)),
        (i) => i,
    );
    $: levelForFixedParent =
        parentId == null ? null : (categoriesById[parentId]?.levelInHierarchy ?? null);

    $: setInnerValuesFromValue($value, levels);
    $: setValueFromInnerValues($innerValues, levels);
    $: {
        levels;
        form.dispatch(validate);
    }

    function optionsForLevel(level: number) {
        let levelPrevValue = $innerValues.at(level - 1 - 1) ?? null;

        return sortBy(
            categories
                .filter((c) => {
                    if (c.levelInHierarchy != level) {
                        return false;
                    }

                    if (level === 1) {
                        return true;
                    }

                    if (levelPrevValue != null && c.parentId != null) {
                        return levelPrevValue === c.parentId;
                    }

                    return false;
                })
                .map((c) => {
                    return {
                        value: c.id,
                        key: `${c.id}`,
                        label: c.name,
                    };
                }),
            (c) => c.label,
        );
    }

    function setValueFromInnerValues(innerValues: Value[], levels: number[]) {
        if (levels.length > 0) {
            const setValue = (v: Value) =>
                form.dispatch((state) => {
                    const value = field.getValue(state.values);
                    if (v !== value) {
                        return form.actions().changeValue(v, field)(state);
                    } else {
                        return state;
                    }
                });

            setValue(last(keepNotNull(innerValues)) ?? null);
        }
    }

    function innerValuesFromValue(
        value: Value,
        parentId: Value,
        levels: number[],
    ): Value[] {
        const innerValues: Value[] = levels.map(() => null);

        if (value != null) {
            let category = categoriesById[value] ?? null;
            while (category != null) {
                innerValues[category.levelInHierarchy - 1] = category.id;
                category =
                    category.parentId == null
                        ? null
                        : (categoriesById[category.parentId] ?? null);
            }
        }

        if (parentId != null) {
            let category = categoriesById[parentId] ?? null;
            while (category != null) {
                innerValues[category.levelInHierarchy - 1] = category.id;
                category =
                    category.parentId == null
                        ? null
                        : (categoriesById[category.parentId] ?? null);
            }
        }

        return normalizeInnerValues(innerValues);
    }

    function setInnerValuesFromValue(value: Value, levels: number[]) {
        if (levels.length > 0) {
            innerForm.dispatch((state) => {
                return innerForm
                    .actions()
                    .changeValue(innerValuesFromValue(
                            value,
                            parentId,
                            levels,
                        ), innerFields.self)(state);
            });
        }
    }

    function validate(state: FormState<TValues, string>) {
        const value = field.getValue(state.values);
        const category = value == null ? null : (categoriesById[value] ?? null);

        if (category == null) {
            return state;
        }

        return match(validation)
            .with(P.nullish, () => {
                return state;
            })
            .with('force-leaf', () => {
                if (category.hasChildren) {
                    state = form
                        .actions()
                        .addError(field, `Vous devez choisir une sous catégorie.`)(state);
                }

                return state;
            })
            .with({ type: 'minimum-level', level: P.select() }, (minimumLevel) => {
                if (category.levelInHierarchy < minimumLevel) {
                    state = form
                        .actions()
                        .addError(field, `Vous devez choisir une sous catégorie.`)(state);
                }

                return state;
            })
            .exhaustive();
    }

    onMount(() => {
        const off = form.addValidator(validate);
        return () => off();
    });
</script>

{#if categories.length === 0}
    <div class={className}>
        <Spinner />
    </div>
{:else}
    <div class={cx(className, 'flex flex-col gap-4')}>
        {#each $innerValues as value, index (index)}
            {@const level = index + 1}
            {#if levelForFixedParent != null && level <= levelForFixedParent}
                {@const category = value == null ? null : (categoriesById[value] ?? null)}
                {#if category != null}
                    <div class="control-form bg-gray-200">{category.name}</div>
                {/if}
            {:else}
                {@const options = optionsForLevel(level)}
                {#if options.length > 0}
                    <Select
                        form={innerForm}
                        field={innerFields.item(index)}
                        required={false}
                        showChevron
                        {options}
                    />
                {/if}
            {/if}
        {/each}
    </div>
{/if}
